import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Plus, X, Search, Users, Mail, Phone, Globe, Tag, ChevronLeft, ChevronRight,
  Trash2, Edit3, ExternalLink, Filter, Briefcase, TrendingUp, Package, Crosshair,
  GripVertical, AlertOctagon,
} from "lucide-react";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners,
  useDraggable, useDroppable,
} from "@dnd-kit/core";
import { useLanguage } from "../i18n/LanguageContext";
import { useIsAdmin } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─────────────────────────────────────────────────────────
// Pipeline configuration
// ─────────────────────────────────────────────────────────
const STAGES = [
  { id: "new",         label: "New",         color: "#64748B" },
  { id: "contacted",   label: "Contacted",   color: "#0EA5E9" },
  { id: "qualified",   label: "Qualified",   color: "#0891B2" },
  { id: "proposal",    label: "Proposal",    color: "#FBBF24" },
  { id: "negotiation", label: "Negotiation", color: "#EA580C" },
  { id: "won",         label: "Won",         color: "#059669" },
  { id: "lost",        label: "Lost",        color: "#DC2626" },
];

const TYPES = [
  { id: "prospect",           label: "Potenziale Cliente", color: "#059669", icon: Crosshair },
  { id: "customer",           label: "Cliente",            color: "#EA580C", icon: Users },
  { id: "potential_supplier", label: "Potenziale Fornitore", color: "#0EA5E9", icon: Package },
  { id: "supplier",           label: "Fornitore",          color: "#7C3AED", icon: Briefcase },
  { id: "competitor",         label: "Competitor",         color: "#DC2626", icon: TrendingUp },
];

const EMPTY_CARD = {
  type: "prospect", stage: "new",
  company: "", contact: "", email: "", phone: "", website: "",
  value: 0, owner: "", notes: "", tags: [],
};

// ─────────────────────────────────────────────────────────
// Card editor modal
// ─────────────────────────────────────────────────────────
function CardEditor({ card, onClose, onSave }) {
  const isNew = !card?.id;
  const [form, setForm] = useState(card || EMPTY_CARD);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex" onClick={onClose}>
      <div className="flex-1 bg-black/70 backdrop-blur-sm" />
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-[600px] bg-[#0A0C10] border-l-2 border-[#00E5FF] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.04] bg-[#0D0F12]">
          <h2 className="text-lg font-bold tracking-wide">{isNew ? "Nuova Card CRM" : "Modifica Card"}</h2>
          <button type="button" onClick={onClose} data-testid="crm-editor-close" className="p-2 text-[#A0AEC0] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
          <Field label="Tipo *">
            <select value={form.type} onChange={e => set("type", e.target.value)} required
              data-testid="crm-editor-type"
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none">
              {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Stage *">
            <select value={form.stage} onChange={e => set("stage", e.target.value)} required
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none">
              {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Azienda *">
            <input value={form.company} onChange={e => set("company", e.target.value)} required
              data-testid="crm-editor-company"
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
          </Field>
          <Field label="Contatto referente">
            <input value={form.contact} onChange={e => set("contact", e.target.value)}
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email">
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
            </Field>
            <Field label="Telefono">
              <input value={form.phone} onChange={e => set("phone", e.target.value)}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Sito Web">
              <input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…"
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
            </Field>
            <Field label="Valore (EUR)">
              <input type="number" value={form.value} onChange={e => set("value", parseInt(e.target.value, 10) || 0)}
                className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
            </Field>
          </div>
          <Field label="Owner">
            <input value={form.owner} onChange={e => set("owner", e.target.value)}
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
          </Field>
          <Field label="Tag (separati da virgola)">
            <input
              value={Array.isArray(form.tags) ? form.tags.join(", ") : ""}
              onChange={e => set("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none" />
          </Field>
          <Field label="Note">
            <textarea rows={4} value={form.notes} onChange={e => set("notes", e.target.value)}
              className="w-full bg-[#050505] border border-white/[0.04] text-white px-3 py-2 focus:border-[#00E5FF] outline-none resize-none" />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/[0.04] bg-[#0D0F12]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-white/60 hover:text-white">Annulla</button>
          <button type="submit" disabled={saving} data-testid="crm-editor-save"
            className="px-5 py-2 bg-[#00E5FF] text-black font-bold text-xs hover:bg-[#00B3CC] disabled:opacity-40">
            {saving ? "Salvataggio…" : isNew ? "Crea Card" : "Salva Modifiche"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] text-[#A0AEC0] tracking-widest uppercase mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Kanban card (draggable when canEdit)
// ─────────────────────────────────────────────────────────
function KanbanCard({ card, typeDef, onMove, onEdit, onDelete, canEdit, isDragging: forcedDragging }) {
  const stageIdx = STAGES.findIndex(s => s.id === card.stage);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    data: { stage: card.stage, card },
    disabled: !canEdit,
  });
  const dragging = forcedDragging || isDragging;
  return (
    <div
      ref={setNodeRef}
      data-testid="crm-card"
      style={{ opacity: dragging ? 0.4 : 1 }}
      className="bg-[#0D0F12] border-l-2 border-white/[0.04] hover:border-[#00E5FF] transition-colors p-3 group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {canEdit && (
            <button
              {...attributes} {...listeners}
              data-testid={`crm-card-drag-${card.id}`}
              className="cursor-grab active:cursor-grabbing text-[#6B7280] hover:text-[#00E5FF] flex-shrink-0"
              aria-label="Trascina card"
            >
              <GripVertical className="w-3 h-3" />
            </button>
          )}
          <span style={{ backgroundColor: typeDef.color }} className="w-2 h-2 rounded-full flex-shrink-0" />
          <span className="text-[9px] uppercase tracking-widest font-bold flex-shrink-0" style={{ color: typeDef.color }}>{typeDef.label}</span>
        </div>
        {canEdit && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <a
              href={`/entity-loss-simulation?name=${encodeURIComponent(card.company)}`}
              className="text-[#A0AEC0] hover:text-[#FF1744]"
              title="Simula perdita entità"
              data-testid="crm-card-simulate-loss"
            ><AlertOctagon className="w-3 h-3" /></a>
            <button onClick={() => onEdit(card)} className="text-[#A0AEC0] hover:text-[#00E5FF]"><Edit3 className="w-3 h-3" /></button>
            <button onClick={() => onDelete(card)} className="text-[#A0AEC0] hover:text-[#FF3B30]"><Trash2 className="w-3 h-3" /></button>
          </div>
        )}
      </div>
      <p className="text-sm font-bold text-white truncate">{card.company}</p>
      {card.contact && <p className="text-[11px] text-[#CBD5E1] truncate mt-0.5">{card.contact}</p>}
      <div className="text-[10px] text-[#A0AEC0] mt-2 space-y-0.5">
        {card.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{card.email}</div>}
        {card.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{card.phone}</div>}
        {card.website && (
          <a href={card.website} target="_blank" rel="noopener noreferrer"
            data-testid="crm-card-website"
            className="flex items-center gap-1 text-[#00E5FF] hover:underline">
            <Globe className="w-3 h-3" />{card.website.replace(/^https?:\/\//, "")}<ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
      {Array.isArray(card.tags) && card.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {card.tags.slice(0, 3).map((t, i) => (
            <span key={`${t}-${i}`} className="text-[9px] bg-[#1F242D] text-[#CBD5E1] px-1.5 py-0.5">{t}</span>
          ))}
        </div>
      )}
      {card.value > 0 && (
        <div className="mt-2 text-[11px] font-bold text-[#00FF66] font-mono">€ {Number(card.value).toLocaleString("it-IT")}</div>
      )}
      {canEdit && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.04]">
          <button onClick={() => onMove(card, -1)} disabled={stageIdx === 0}
            data-testid="crm-card-stage-prev"
            className="text-[#A0AEC0] hover:text-[#00E5FF] disabled:opacity-20"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-[9px] text-[#A0AEC0] tracking-widest uppercase">{card.owner || "—"}</span>
          <button onClick={() => onMove(card, +1)} disabled={stageIdx === STAGES.length - 1}
            data-testid="crm-card-stage-next"
            className="text-[#A0AEC0] hover:text-[#00E5FF] disabled:opacity-20"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Stage column (droppable)
// ─────────────────────────────────────────────────────────
function StageColumn({ stage, count, canDrop, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, disabled: !canDrop });
  return (
    <div data-testid={`crm-stage-${stage.id}`} className="w-72 flex-shrink-0">
      <div className="flex items-center justify-between px-3 py-2 mb-3 bg-[#0D0F12] border-l-2" style={{ borderColor: stage.color }}>
        <span className="text-xs font-bold uppercase tracking-widest text-white">{stage.label}</span>
        <span className="text-[10px] text-[#A0AEC0] font-mono">{count}</span>
      </div>
      <div
        ref={setNodeRef}
        data-testid={`crm-drop-${stage.id}`}
        className="space-y-2 min-h-[80px] p-1 transition-colors"
        style={{
          backgroundColor: isOver ? `${stage.color}15` : "transparent",
          outline: isOver ? `1px dashed ${stage.color}` : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────
export default function IntegratedCRM() {
  const { t } = useLanguage();
  const isAdmin = useIsAdmin();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/crm/cards`, { withCredentials: true });
      setCards(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter(c => {
      if (typeFilter !== "all" && c.type !== typeFilter) return false;
      if (!q) return true;
      return [c.company, c.contact, c.email].some(
        v => (v || "").toLowerCase().includes(q),
      );
    });
  }, [cards, typeFilter, search]);

  const byStage = useMemo(() => {
    const out = {};
    STAGES.forEach(s => { out[s.id] = []; });
    filtered.forEach(c => { if (out[c.stage]) out[c.stage].push(c); });
    return out;
  }, [filtered]);

  const counts = useMemo(() => {
    const out = { all: cards.length };
    TYPES.forEach(t => { out[t.id] = cards.filter(c => c.type === t.id).length; });
    return out;
  }, [cards]);

  const saveCard = async (payload) => {
    if (payload.id) {
      await axios.put(`${API}/crm/cards/${payload.id}`, payload, { withCredentials: true });
    } else {
      await axios.post(`${API}/crm/cards`, payload, { withCredentials: true });
    }
    setEditing(null);
    await load();
  };

  const moveCard = async (card, delta) => {
    const idx = STAGES.findIndex(s => s.id === card.stage);
    const next = idx + delta;
    if (next < 0 || next >= STAGES.length) return;
    await axios.put(`${API}/crm/cards/${card.id}`, { stage: STAGES[next].id }, { withCredentials: true });
    await load();
  };

  const moveCardToStage = async (cardId, newStage) => {
    const card = cards.find(c => c.id === cardId);
    if (!card || card.stage === newStage) return;
    // Optimistic update
    setCards(prev => prev.map(c => c.id === cardId ? { ...c, stage: newStage } : c));
    try {
      await axios.put(`${API}/crm/cards/${cardId}`, { stage: newStage }, { withCredentials: true });
    } catch (err) {
      console.error("Move failed", err);
      await load(); // rollback via reload
    }
  };

  // DnD state
  const [activeCard, setActiveCard] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = (event) => {
    const c = cards.find(x => x.id === event.active.id);
    setActiveCard(c || null);
  };
  const handleDragEnd = (event) => {
    setActiveCard(null);
    const { active, over } = event;
    if (!over) return;
    const newStage = over.id;
    if (!STAGES.find(s => s.id === newStage)) return;
    moveCardToStage(active.id, newStage);
  };

  const deleteCard = async (card) => {
    if (!window.confirm(`Eliminare la card "${card.company}"?`)) return;
    await axios.delete(`${API}/crm/cards/${card.id}`, { withCredentials: true });
    await load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] mb-1">CRM · KANBAN PIPELINE</div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{fontFamily: "Chivo, sans-serif"}}>Integrated CRM</h1>
          <p className="text-xs text-[#A0AEC0] mt-1">{cards.length} card · {filtered.length} visibili</p>
        </div>
        {isAdmin && (
          <button data-testid="crm-add-card"
            onClick={() => setEditing(EMPTY_CARD)}
            className="flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-4 py-2 text-xs hover:bg-[#00B3CC]">
            <Plus className="w-4 h-4" /> Nuova Card
          </button>
        )}
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-[#0D0F12] border border-white/[0.04] p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A0AEC0]"><Filter className="w-3 h-3" />Tipo</div>
        <button data-testid="crm-filter-all"
          onClick={() => setTypeFilter("all")}
          className={`px-3 py-1 text-xs font-bold uppercase tracking-widest border ${typeFilter === "all" ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "border-white/[0.04] text-[#CBD5E1] hover:border-[#00E5FF]"}`}>
          Tutti ({counts.all})
        </button>
        {TYPES.map(tp => (
          <button key={tp.id} data-testid={`crm-filter-${tp.id}`}
            onClick={() => setTypeFilter(tp.id)}
            style={typeFilter === tp.id ? { backgroundColor: tp.color, color: "#000", borderColor: tp.color } : { borderColor: tp.color, color: tp.color }}
            className="px-3 py-1 text-xs font-bold uppercase tracking-widest border flex items-center gap-1">
            <tp.icon className="w-3 h-3" />{tp.label} ({counts[tp.id] || 0})
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A0AEC0]" />
          <input data-testid="crm-search"
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cerca azienda, contatto, email…"
            className="bg-[#050505] border border-white/[0.04] text-white pl-9 pr-3 py-1.5 text-xs w-64 focus:outline-none focus:border-[#00E5FF]" />
        </div>
      </div>

      {/* Kanban board */}
      {loading ? (
        <div className="text-center text-[#A0AEC0] py-20">Caricamento…</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-3 min-w-max">
              {STAGES.map(stage => (
                <StageColumn key={stage.id} stage={stage} count={byStage[stage.id].length} canDrop={isAdmin}>
                  {byStage[stage.id].length === 0 ? (
                    <div className="text-center text-[10px] text-[#A0AEC0] py-6 italic border border-dashed border-white/[0.04]">Vuota</div>
                  ) : byStage[stage.id].map(card => (
                    <KanbanCard key={card.id}
                      card={card}
                      typeDef={TYPES.find(tp => tp.id === card.type) || TYPES[0]}
                      onMove={moveCard} onEdit={setEditing} onDelete={deleteCard}
                      canEdit={isAdmin}
                    />
                  ))}
                </StageColumn>
              ))}
            </div>
          </div>
          <DragOverlay dropAnimation={null}>
            {activeCard ? (
              <div className="w-72">
                <KanbanCard
                  card={activeCard}
                  typeDef={TYPES.find(tp => tp.id === activeCard.type) || TYPES[0]}
                  onMove={() => {}} onEdit={() => {}} onDelete={() => {}}
                  canEdit={false}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {editing && (
        <CardEditor
          card={editing}
          onClose={() => setEditing(null)}
          onSave={saveCard}
        />
      )}
    </div>
  );
}
